import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "../../../../api/apiClient";
import { apiRoutes } from "../../../../configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface DepositPointLimitItem {
  id: number;
  loyaltyLevelId: number;
  level?: number;
  title?: string;
  depositPointsLimit?: string;
  pointsLimit?: string;
  updatedAt?: string | null;
  period?: string;
}

interface DepositPointLimitsState {
  list: DepositPointLimitItem[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  period: string;
  payload?: DepositPointLimitItem;
  processingItemId?: number;
}

const validationError = (error: any): ApiErrorPayload => {
  const apiError = error?.data?.error ?? error?.data;
  if (apiError?.code) return apiError;
  return {
    code: "InternalError",
    fieldErrors: [],
    globalErrors: [{ code: "unknownError" }],
  };
};

export const getDepositPointLimits = createAsyncThunk(
  "deposit-point-limits/list",
  async (data: {
    limit: number;
    offset: number;
    title?: string;
    level?: string;
  }) => {
    const result = await instance.post(apiRoutes.depositPointLimits, data);
    return result.data;
  },
);

export const getDepositPointLimit = createAsyncThunk(
  "deposit-point-limits/item",
  async (id: string) => {
    const result = await instance.get(`${apiRoutes.depositPointLimitItem}/${id}`);
    return result.data;
  },
);

export const createDepositPointLimit = createAsyncThunk<
  boolean,
  { loyaltyLevelId: number; pointsLimit: string },
  { rejectValue: ApiErrorPayload }
>("deposit-point-limits/create", async (data, { rejectWithValue }) => {
  try {
    await instance.post(apiRoutes.depositPointLimitCreate, data);
    return true;
  } catch (error) {
    return rejectWithValue(validationError(error));
  }
});

export const updateDepositPointLimit = createAsyncThunk<
  boolean,
  { id: number; loyaltyLevelId: number; pointsLimit: string },
  { rejectValue: ApiErrorPayload }
>("deposit-point-limits/update", async (data, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.depositPointLimitUpdate}/${data.id}`, data);
    return true;
  } catch (error) {
    return rejectWithValue(validationError(error));
  }
});

export const deleteDepositPointLimit = createAsyncThunk(
  "deposit-point-limits/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.depositPointLimitDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const setDepositPointLimitPeriod = createAsyncThunk<
  { period: string },
  string,
  { rejectValue: ApiErrorPayload }
>("deposit-point-limits/period", async (period, { rejectWithValue }) => {
  try {
    const result = await instance.put(apiRoutes.depositPointLimitPeriod, { period });
    return result.data;
  } catch (error) {
    return rejectWithValue(validationError(error));
  }
});

const initialState: DepositPointLimitsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  period: "",
};

export const depositPointLimitsSlice = createSlice({
  name: "deposit-point-limits",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleClear: (state) => {
      state.activePage = 1;
      state.isListFetched = false;
      state.list = [];
      state.totalItems = 0;
      state.totalPages = 0;
    },
    handleClearFields: (state) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleUpdateFields: (state, action: PayloadAction<Partial<DepositPointLimitItem>>) => {
      state.payload = { ...state.payload, ...action.payload } as DepositPointLimitItem;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDepositPointLimits.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
      state.period = action.payload?.period ?? "";
    });
    builder.addCase(getDepositPointLimits.rejected, (state) => {
      state.isListFetched = true;
      state.list = [];
    });
    builder.addCase(getDepositPointLimit.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      if (action.payload?.period) state.period = action.payload.period;
    });
    builder.addCase(createDepositPointLimit.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createDepositPointLimit.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createDepositPointLimit.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateDepositPointLimit.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateDepositPointLimit.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateDepositPointLimit.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteDepositPointLimit.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deleteDepositPointLimit.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteDepositPointLimit.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(setDepositPointLimitPeriod.fulfilled, (state, action) => {
      state.period = action.payload?.period ?? state.period;
    });
  },
});

export const {
  handlePaginate,
  handleClear,
  handleClearFields,
  handleUpdateFields,
} = depositPointLimitsSlice.actions;
